import React, { useEffect, useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const SubscriptionsAndPayoutsPage = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const apiBaseURL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('employeeToken');
      if (!token) {
        window.location.href = "/employee-login";
        return;
      }

      try {
        const response = await axios.get(`${apiBaseURL}/api/subscriptions-and-payouts`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        setSubscriptions(response.data.subscriptions || []);
        setPayouts(response.data.payouts || []);
      } catch (err) {
        setError('Failed to fetch data.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [apiBaseURL]);

  const formatIST = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Totals
  const totalSubscriptions = subscriptions.length;
  const totalUniqueSubscribers = new Set(subscriptions.map(sub => sub.freelancer_id)).size;

  const totalFeesBeforeGST = subscriptions.reduce((sum, sub) => sum + Number(sub.subscription_fees_before_gst || 0), 0);
  const totalGSTAmount = subscriptions.reduce((sum, sub) => sum + Number(sub.subscription_gst_amount || 0), 0);
  const totalFeesWithGST = subscriptions.reduce((sum, sub) => sum + Number(sub.subscription_fees_with_gst || 0), 0);

  const totalEarnings = payouts.reduce((sum, p) => sum + Number(p.total_earnings || 0), 0);
  const totalEarningsAlreadyWithdrawn = payouts.reduce((sum, p) => sum + Number(p.earnings_already_withdrawn || 0), 0);
  const totalEarningsYetToWithdraw = payouts.reduce((sum, p) => sum + Number(p.earnings_yet_to_withdraw || 0), 0);

  // ✅ Fixed: use Fees Before GST for Spare Balance
  const spareMoney = totalFeesBeforeGST - totalEarnings;

  const handleDownloadExcel = () => {
    const wb = XLSX.utils.book_new();
    const timestamp = new Date();
    const filename = `Subscriptions_And_Payouts_${timestamp.toISOString().slice(0, 19).replace(/[:T]/g, "-")}.xlsx`;

    const downloadHeader = [
      ["OrderMinds Freelancing Solutions LLP"],
      ["Subscriptions and Payouts Report"],
      [`Downloaded on: ${formatIST(timestamp)}`],
      []
    ];

    const subscriptionsSheet = [
      ...downloadHeader,
      ["Summary Totals"],
      ["Total Subscriptions", totalSubscriptions],
      ["Total Subscribers (Unique Freelancers)", totalUniqueSubscribers],
      ["Total SUBSCRIPTION FEES WITH GST", `₹ ${totalFeesWithGST}`],
      ["Total SUBSCRIPTION FEES BEFORE GST", `₹ ${totalFeesBeforeGST}`],
      ["Total SUBSCRIPTION GST AMOUNT", `₹ ${totalGSTAmount}`],
      ["Total Earnings", `₹ ${totalEarnings}`],
      ["Total Earnings - Already Withdrawn", `₹ ${totalEarningsAlreadyWithdrawn}`],
      ["Total Earnings - Yet to be Withdrawn", `₹ ${totalEarningsYetToWithdraw}`],
      ["Spare Balance (Fees Before GST - Total Earnings)", `₹ ${spareMoney}`],
      []
    ];

    const subSheet = XLSX.utils.aoa_to_sheet(subscriptionsSheet);
    XLSX.utils.book_append_sheet(wb, subSheet, "Summary");

    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([wbout], { type: 'application/octet-stream' }), filename);
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  return (
    <div className="flex-1 p-6 bg-gray-100 ml-[16rem]">
      <h2 className="text-2xl font-semibold mb-6">Subscriptions and Payouts Summary</h2>

      <div className="space-y-6 mb-6">

        {/* Row 1 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <SummaryCard title="Total Subscribers (Freelancers)" value={totalUniqueSubscribers} bgColor="bg-blue-400" />
          <SummaryCard title="Total Subscriptions" value={totalSubscriptions} bgColor="bg-blue-500" />
          <div></div>
        </div>

        {/* Row 2 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <SummaryCard title="Fees With GST" value={`₹ ${totalFeesWithGST}`} bgColor="bg-purple-500" />
          <SummaryCard title="Fees Before GST" value={`₹ ${totalFeesBeforeGST}`} bgColor="bg-purple-400" />
          <SummaryCard title="GST Collected" value={`₹ ${totalGSTAmount}`} bgColor="bg-purple-400" />
        </div>

        {/* Row 3 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <SummaryCard title="Total Earnings" value={`₹ ${totalEarnings}`} bgColor="bg-green-500" />
          <SummaryCard title="Withdrawn" value={`₹ ${totalEarningsAlreadyWithdrawn}`} bgColor="bg-green-400" />
          <SummaryCard title="Yet to Withdraw" value={`₹ ${totalEarningsYetToWithdraw}`} bgColor="bg-green-400" />
        </div>

        {/* Row 4: One card aligned left */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-1">
            <SummaryCard title="Spare Balance(Fees Before GST - Total Earnings)" value={`₹ ${spareMoney}`} bgColor="bg-gray-600" />
          </div>
        </div>

      </div>

      <button
        onClick={handleDownloadExcel}
        className="mb-6 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
      >
        📅 Download Excel Summary
      </button>
    </div>
  );
};

const SummaryCard = ({ title, value, bgColor = "bg-white" }) => (
  <div className={`${bgColor} text-white p-4 rounded shadow`}>
    <h4 className="text-lg font-medium mb-2">{title}</h4>
    <p className="text-2xl font-bold">{value}</p>
  </div>
);

export default SubscriptionsAndPayoutsPage;
